import React, { createContext, useContext, useCallback } from "react";
import { useToast } from "../components/common/PixelToast";

/**
 * NotificationContext
 * Global notification helpers — wraps useToast + game-specific events.
 *
 * Usage:
 *   const { notify, notifyLevelUp, notifyBadge, notifyXP } = useNotification();
 */

export const NotificationContext = createContext({
  notify:        () => {},
  notifyLevelUp: () => {},
  notifyBadge:   () => {},
  notifyXP:      () => {},
});

export const NotificationProvider = ({ children }) => {
  const { showToast } = useToast();

  const notify = useCallback((options) => {
    showToast(options);
  }, [showToast]);

  const notifyLevelUp = useCallback((oldLevel, newLevel, title, perks = []) => {
    try {
      localStorage.setItem(
        "pending_level_up",
        JSON.stringify({ old: oldLevel, new: newLevel, title, perks })
      );
    } catch { /* silent */ }
    showToast({
      type: "xp",
      message: `⬆️ LEVEL UP! Lv.${oldLevel} → Lv.${newLevel} · ${title}`,
      duration: 5000,
    });
  }, [showToast]);

  const notifyBadge = useCallback((badge) => {
    showToast({
      type:     "xp",
      message:  `🏅 نشان جدید: ${badge?.name ?? badge}`,
      icon:     badge?.icon,
      duration: 4000,
    });
  }, [showToast]);

  const notifyXP = useCallback((amount, multiplier = 1) => {
    const total = Math.round(amount * multiplier);
    const text  = multiplier > 1
      ? `+${total} XP ⚡ (x${multiplier} استریک!)`
      : `+${total} XP ⚡`;
    showToast({ type: "xp", message: text, duration: 2500 });
    try {
      const el = document.createElement("div");
      el.textContent = `+${total} XP`;
      el.style.cssText = "position:fixed;top:20%;left:50%;transform:translateX(-50%);font-family:'Press Start 2P',monospace;font-size:1.2rem;color:#ffd700;text-shadow:2px 2px 0 #7a6200;pointer-events:none;z-index:9999;animation:xpfloat 1.8s ease-out forwards";
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 1800);
    } catch { /* silent */ }
  }, [showToast]);

  return (
    <NotificationContext.Provider value={{ notify, notifyLevelUp, notifyBadge, notifyXP }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);

export default NotificationContext;